import { FC } from "react";
import {
  Box,
  Drawer,
  Typography,
  TextField,
  IconButton,
  useTheme
} from "@mui/material";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { types, Button, toast } from "@vilocnv/allsetra-core";
import { useTranslation } from "react-i18next";

interface DetourRouteModalProps {
  open: boolean;
  onClose: () => void;
  ride: types.IObjectRide | null;
  onSubmit: (values: DetourRouteFormValues) => void | Promise<void>;
}

export interface DetourRouteFormValues {
  detourKilometers: string;
  detourReason: string;
  changeReason: string;
}

const DetourRouteModal: FC<DetourRouteModalProps> = ({
  open,
  onClose,
  ride,
  onSubmit
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const currentRideMileage = ride?.mileage || 0;
  const existingDetourMileage = (ride as any)?.detourMileage || 0;
  const existingDetourReason = (ride as any)?.detourReason || "";
  const existingChangeReason = (ride as any)?.changeReason || "";

  const maxAllowedMileage = Math.round(currentRideMileage * 100) / 100;

  const hasExistingData =
    existingDetourMileage > 0 || existingDetourReason !== "";

  const validationSchema = (t: any) =>
    Yup.object({
      detourKilometers: Yup.number()
        .required(t("validation.fieldRequired"))
        .min(0, t("validation.cannotBeNegative"))
        .test(
          "max-mileage",
          t("validation.detourKilometersExceedsRideMileage", {
            mileage: currentRideMileage.toLocaleString()
          }),
          (value) => value === undefined || value <= maxAllowedMileage
        )
        .typeError(t("validation.mustBeNumber")),
      detourReason: hasExistingData
        ? Yup.string()
        : Yup.string()
            .trim()
            .min(1, t("validation.fieldRequired"))
            .required(t("validation.fieldRequired")),
      changeReason: Yup.string()
        .trim()
        .min(1, t("validation.fieldRequired"))
        .required(t("validation.fieldRequired"))
    });

  const initialValues: DetourRouteFormValues = {
    detourKilometers: existingDetourMileage
      ? String(existingDetourMileage)
      : "",
    detourReason: existingDetourReason || "",
    changeReason: existingChangeReason || ""
  };

  const formatRideDate = (ride: types.IObjectRide | null) => {
    if (!ride?.startDate || !ride?.endDate) return "";

    const startDate = new Date(ride.startDate);
    const endDate = new Date(ride.endDate);

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ];
    const month = months[startDate.getMonth()];
    const day = startDate.getDate();
    const year = startDate.getFullYear();

    const daySuffix = (d: number) => {
      if (d > 3 && d < 21) return "th";
      switch (d % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    const formatTime = (date: Date) => {
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    };

    const dateStr = `${month} ${day}${daySuffix(day)}, ${year}`;
    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);

    return `${dateStr}, ${startTime} - ${endTime}`;
  };

  const handleSubmit = async (
    values: DetourRouteFormValues,
    formikHelpers: FormikHelpers<DetourRouteFormValues>
  ) => {
    const isSameData =
      hasExistingData &&
      Number(values.detourKilometers) === existingDetourMileage &&
      values.detourReason === existingDetourReason &&
      values.changeReason === existingChangeReason;

    if (isSameData) {
      toast.error(t("messages.noChangesDetected"));
      formikHelpers.setSubmitting(false);
      return;
    }

    try {
      const submissionValues = {
        ...values,
        detourReason: values.detourReason?.trim() || ""
      };
      await onSubmit(submissionValues);
      onClose();
    } catch (error) {
      console.error("Error submitting detour route:", error);
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "400px",
          height: "100vh",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid #E0E0E0"
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "24px",
                color: "#323947"
              }}
            >
              {t("titles.detourRouteForRide")} <br />
              {formatRideDate(ride)}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ mt: -1, mr: -1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Form Content */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(t)}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldTouched,
            setFieldValue,
            isSubmitting,
            isValid
          }) => {
            const hasChanges =
              Number(values.detourKilometers) !== existingDetourMileage ||
              values.detourReason !== existingDetourReason ||
              values.changeReason !== existingChangeReason;

            const isFormValid = isValid && hasChanges;

            return (
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%"
                }}
              >
                <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
                  {/* Number of detour kilometres */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      {t("titles.numberOfDetourKilometres")}
                    </Typography>
                    <TextField
                      fullWidth
                      name="detourKilometers"
                      type="number"
                      placeholder={currentRideMileage.toLocaleString()}
                      value={values.detourKilometers}
                      onChange={(e) => {
                        const inputValue = e.target.value;
                        const numericValue = Number(inputValue);

                        if (numericValue > maxAllowedMileage) {
                          const correctedValue = maxAllowedMileage.toFixed(1);
                          setFieldValue("detourKilometers", correctedValue);
                        } else {
                          handleChange(e);
                        }
                      }}
                      onBlur={(e) => {
                        handleBlur(e);
                        setFieldTouched("detourKilometers", true);

                        if (e.target.value === "" || e.target.value === null) {
                          setFieldValue("detourKilometers", "0");
                        }
                      }}
                      error={
                        touched.detourKilometers &&
                        Boolean(errors.detourKilometers)
                      }
                      helperText={
                        touched.detourKilometers && errors.detourKilometers
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px"
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider
                        },
                        "& .MuiOutlinedInput-input": {
                          fontSize: "14px",
                          padding: "12px"
                        }
                      }}
                    />
                  </Box>

                  {/* Detour Reason */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      {t("titles.detourReason")}
                      {!hasExistingData && (
                        <Typography
                          component="span"
                          sx={{ color: "error.main", ml: 0.5 }}
                        >
                          *
                        </Typography>
                      )}
                    </Typography>
                    <TextField
                      fullWidth
                      name="detourReason"
                      multiline
                      rows={2}
                      placeholder={t("titles.whatIsTheReasonForTheDetourRoute")}
                      value={values.detourReason}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.detourReason && Boolean(errors.detourReason)
                      }
                      helperText={touched.detourReason && errors.detourReason}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px"
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider
                        },
                        "& .MuiInputBase-inputMultiline": {
                          fontSize: "14px"
                        }
                      }}
                    />
                  </Box>

                  {/* Change Reason */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      {t("titles.changeReason")}
                      <Typography
                        component="span"
                        sx={{ color: "error.main", ml: 0.5 }}
                      >
                        *
                      </Typography>
                    </Typography>
                    <TextField
                      fullWidth
                      name="changeReason"
                      placeholder={t("titles.reasonForThisChange")}
                      value={values.changeReason}
                      multiline
                      rows={2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.changeReason && Boolean(errors.changeReason)
                      }
                      helperText={touched.changeReason && errors.changeReason}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px"
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.divider
                        },
                        "& .MuiInputBase-inputMultiline": {
                          fontSize: "14px"
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* Footer Actions */}
                <Box
                  sx={{
                    p: 3,
                    borderTop: "1px solid #E0E0E0",
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end"
                  }}
                >
                  <Button
                    onClick={onClose}
                    disabled={isSubmitting}
                    variant="text"
                    text={t("buttonsText.cancel")}
                    sx={{
                      color: theme.palette.secondary.main
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !isFormValid}
                    text={t("buttonsText.save")}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.contrastText
                      }
                    }}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Drawer>
  );
};

export default DetourRouteModal;
